import { useEffect, useRef } from 'react';

import type { RefObject } from './types';

const useSlider = () => {
    const ref = useRef<HTMLDivElement>(null);
    const offsetRef = useRef({ x: 0, y: 0 });
    const scrollRef = useRef({ x: 0, y: 0 });

    const isDragging = useRef(false);

    const getCurrentCoordinates = (
        event: MouseEvent,
        ref: RefObject<HTMLDivElement | null>
    ): { x: number; y: number } => {
        if (!ref.current) {
            return { x: 0, y: 0 };
        }

        const x = event.pageX - ref.current.offsetLeft;
        const y = event.pageY - ref.current.offsetTop;

        return { x, y };
    };

    useEffect(() => {
        if (!ref?.current) {
            return;
        }

        const currentRef = ref.current;

        const handleMouseDown = (event: MouseEvent) => {
            isDragging.current = true;

            if (!ref.current) {
                return;
            }

            currentRef.classList.add('dragging');

            const { x, y } = getCurrentCoordinates(event, ref);

            offsetRef.current.x = x;
            offsetRef.current.y = y;
            scrollRef.current.x = currentRef.scrollLeft;
            scrollRef.current.y = currentRef.scrollTop;
        };

        const handleMouseLeave = () => {
            isDragging.current = false;
            currentRef?.classList.remove('dragging');
        };

        const handleMouseMove = (event: MouseEvent) => {
            if (!isDragging.current) {
                return;
            }

            event.preventDefault();

            if (!currentRef) {
                return;
            }

            const { x: startX, y: startY } = offsetRef.current;

            const { x, y } = getCurrentCoordinates(event, ref);

            const moveX = x - startX;
            const moveY = y - startY;

            currentRef.scrollLeft = scrollRef.current.x - moveX;
            currentRef.scrollTop = scrollRef.current.y - moveY;
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            currentRef?.classList.remove('dragging');
        };

        currentRef.addEventListener('mousedown', handleMouseDown);
        currentRef.addEventListener('mouseleave', handleMouseLeave);
        currentRef.addEventListener('mouseup', handleMouseUp);
        currentRef.addEventListener('mousemove', handleMouseMove);

        return () => {
            if (!currentRef) {
                return;
            }

            currentRef.removeEventListener('mousedown', handleMouseDown);
            currentRef.removeEventListener('mouseleave', handleMouseLeave);
            currentRef.removeEventListener('mouseup', handleMouseUp);
            currentRef.removeEventListener('mousemove', handleMouseMove);
        };
    }, [ref]);

    return {
        ref
    };
};

export default useSlider;
